import { IrdFormat, IrdPropertyPresentation, IrdRenderMode } from './resolved';

type Declared<V> = { -readonly [A in keyof IrdPropertyPresentation<V>]?: IrdPropertyPresentation<V>[A] };

/**
 * A switch called bare turns the attribute on. One called with `undefined` — a flag passed through
 * from somewhere optional — declares nothing, as `{ sortable: undefined }` did, rather than `true`.
 */
const flag = (value: [] | [boolean]): boolean | undefined => (value.length ? value[0] : true);

/** What a `property(key, (p) => …)` callback may return: nothing, or the builder it chained on. */
export type IrdPropertyDeclaration<B> = (property: B) => void | B;

/**
 * Declares how one property is presented, on any layer: what `property(key, (p) => …)` hands to
 * its callback. `V` is the property's value type, so a format or render mode for another type does
 * not compile. Only the attributes called are declared; everything else is inherited.
 *
 * What does **not** belong here, and where it goes instead (`docs/metadata/cookbook.md` §1): what the
 * field is — type, allowed values, read-only — is the entity's declaration; what the API permits is a
 * capability on the generated DTO; which properties a view shows, and in which order, is the context
 * builder's `exclude` / `only` / `order`; layout is CSS.
 *
 * `L` is the layer the builder is handed by (`'table'`, `'detail'`, `'form'`, `'entity'`), so a
 * helper written for the table builder does not fit another layer — a subclass alone would, because
 * the repo checks callback parameters bivariantly (`strictFunctionTypes` is off). A helper for every
 * layer is generic in the builder: `<B extends IrdPropertyBuilder<unknown>>(p: B) => …`. The same
 * setting still lets a renderer for a narrower value through, e.g. `(value: string)` on a
 * `string | number` property.
 */
export class IrdPropertyBuilder<V, L extends string = string> {
  declare protected readonly layer: L;
  protected readonly declared: Declared<V> = {};

  public translationKey(translationKey: string): this {
    this.declared.translationKey = translationKey;
    return this;
  }

  public format(format: IrdFormat<V>): this {
    this.declared.format = format;
    return this;
  }

  public renderAs(renderAs: IrdRenderMode<V>): this {
    this.declared.renderAs = renderAs;
    return this;
  }

  public emptyMarker(emptyMarker: string): this {
    this.declared.emptyMarker = emptyMarker;
    return this;
  }

  /**
   * Whether this view lets the property be changed, overriding the schema in either direction. A
   * capability of `false` from the API still wins (ADR-0003).
   */
  public editable(...editable: [] | [boolean]): this {
    this.declared.editable = flag(editable);
    return this;
  }

  /** What the calls declared. Read by the layer, not by a declaration. */
  public toPresentation(): IrdPropertyPresentation<V> {
    return { ...this.declared };
  }
}

/** The table's builder: the attributes only a table reads, beside the ones every layer has. */
export class IrdTablePropertyBuilder<V> extends IrdPropertyBuilder<V, 'table'> {
  /** The view intends to offer sorting here; the API's capability still bounds it (ADR-0003). */
  public sortable(...sortable: [] | [boolean]): this {
    this.declared.sortable = flag(sortable);
    return this;
  }

  /**
   * The path a sort request sends when it is neither the field's own name nor its backend name —
   * a nested `maintenanceRule.id`, say.
   */
  public sortField(sortField: string): this {
    this.declared.sortField = sortField;
    return this;
  }

  /** Cuts overlong text with an ellipsis and shows the whole value as a tooltip. */
  public truncate(...truncate: [] | [boolean]): this {
    this.declared.truncate = flag(truncate);
    return this;
  }

  /** A CSS width the column may not exceed, e.g. `'200px'`. */
  public maxWidth(maxWidth: string): this {
    this.declared.maxWidth = maxWidth;
    return this;
  }
}
